import React, { createContext, useContext, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { X } from "lucide-react";

const LightboxCtx = createContext(null);

export const useLightbox = () => {
  const ctx = useContext(LightboxCtx);
  if (!ctx) throw new Error("useLightbox must be used within VideoLightboxProvider");
  return ctx;
};

export function VideoLightboxProvider({ children }) {
  const [video, setVideo] = useState(null);

  const open = useCallback((v) => setVideo(v), []);
  const close = useCallback(() => setVideo(null), []);

  return (
    <LightboxCtx.Provider value={{ open, close, video }}>
      {children}
      <Dialog open={!!video} onOpenChange={(o) => !o && close()}>
        <DialogContent
          data-testid="video-lightbox"
          className="!max-w-[1200px] w-[95vw] p-0 bg-black border border-white/10 rounded-none overflow-hidden"
        >
          {video && (
            <div>
              <VisuallyHidden.Root>
                <DialogTitle>{video.title}</DialogTitle>
                <DialogDescription>YouTube video player for {video.title}</DialogDescription>
              </VisuallyHidden.Root>
              <button
                onClick={close}
                aria-label="Close"
                data-testid="lightbox-close-btn"
                className="absolute top-3 right-3 z-20 w-10 h-10 bg-black/70 hover:bg-[#C41E3A] text-white flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="relative w-full aspect-video bg-black">
                <iframe
                  data-testid="lightbox-iframe"
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${video.video_id}?autoplay=1&rel=0`}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <div className="px-6 py-5 border-t border-white/10 bg-[#0A0A0A]">
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#C41E3A] mb-2">
                  {video.category === "other" ? "Drop" : video.category}
                </p>
                <h3 className="font-display text-2xl md:text-3xl uppercase leading-tight text-white">
                  {video.title}
                </h3>
                <a
                  href={video.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="lightbox-open-yt"
                  className="mt-3 inline-block text-xs uppercase tracking-[0.25em] text-white/60 hover:text-white"
                >
                  Open on YouTube ↗
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </LightboxCtx.Provider>
  );
}
